// src/pages/etc/mailAlert/MailAlertInboxTab.jsx
// ✅ "수신 알림함" 탭 — IncomeDraftList.jsx(기간 필터+키워드/상태 필터+검색+행
// 클릭→상세) 구조를 그대로 재사용, CSS도 새로 만들지 않고 ExpenseDraftList.module.css
// 그대로 쓴다(이 프로젝트 확립된 관례).
import React, { useEffect, useState } from "react";
import useBackdropClose from "../../../hooks/useBackdropClose";
import api from "../../../api";
import styles from "../../expense/_shared/ExpenseDraftList.module.css";
import { buildNaverMailSearchUrl } from "./naverMailSearchUrl";

const STATUS_LABEL = { pending: "대기", confirmed: "완료", expired: "만료" };
const STATUS_COLOR = { pending: "#856404", confirmed: "#065f46", expired: "#991b1b" };

function receiptSummary(receipts, myUserId) {
  return receipts
    .map((r) => {
      const label = r.user_id === myUserId ? "나" : r.user_name;
      const status = r.status === "confirmed" ? "✓" : STATUS_LABEL[r.status] || r.status;
      return `${label} ${status}`;
    })
    .join(" / ");
}

export default function MailAlertInboxTab() {
  const detailBackdrop = useBackdropClose(() => setDetailRow(null));

  const myUserId = JSON.parse(localStorage.getItem("user") || "{}")?.id ?? null;
  const year = new Date().getFullYear();
  const [fromDate, setFromDate] = useState(`${year}-01-01`);
  const [toDate, setToDate] = useState(`${year}-12-31`);
  const [keywordFilter, setKeywordFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const [rows, setRows] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailRow, setDetailRow] = useState(null);
  const [confirmingId, setConfirmingId] = useState(null);

  async function loadKeywords() {
    try {
      const { data } = await api.get("/mail-alert/keywords/");
      setKeywords(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      console.error("❌ 키워드 목록 불러오기 실패:", err);
    }
  }

  async function loadList() {
    setLoading(true);
    try {
      const { data } = await api.get("/mail-alert/logs/", {
        params: {
          from: fromDate || undefined,
          to: toDate || undefined,
          keyword: keywordFilter || undefined,
          search: search || undefined,
        },
      });
      setRows(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      console.error("❌ 메일 알림 목록 불러오기 실패:", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadKeywords(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadList(); }, [fromDate, toDate, keywordFilter, search]);

  async function openDetail(row) {
    setDetailRow(row);
    if (!row.is_read) {
      try {
        await api.patch(`/mail-alert/logs/${row.id}/mark-read/`);
        setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, is_read: true } : r)));
      } catch (err) {
        console.error("❌ 읽음 처리 실패:", err);
      }
    }
  }

  async function handleConfirm(row) {
    setConfirmingId(row.id);
    try {
      const { data } = await api.post(`/mail-alert/logs/${row.id}/confirm/`);
      setRows((prev) => prev.map((r) => (r.id === row.id ? data : r)));
      setDetailRow((prev) => (prev && prev.id === row.id ? data : prev));
    } catch (err) {
      console.error("❌ 완료 처리 실패:", err);
      alert("완료 처리에 실패했습니다.");
    } finally {
      setConfirmingId(null);
    }
  }

  function openMailSearch(subject) {
    window.open(buildNaverMailSearchUrl(subject), "_blank");
  }

  if (loading && rows.length === 0) return <div className={styles.loading}>불러오는 중…</div>;

  return (
    <div>
      <div className={styles.filters}>
        <div className={styles.filterRow}>
          <span className={styles.filterLabel}>기간</span>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={styles.filterDate} />
          <span className={styles.filterLabel}>~</span>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className={styles.filterDate} />

          <select value={keywordFilter} onChange={(e) => setKeywordFilter(e.target.value)} className={styles.filterSelect}>
            <option value="">전체 키워드</option>
            {keywords.map((k) => (
              <option key={k.id} value={k.keyword}>{k.keyword}</option>
            ))}
          </select>

          <input
            placeholder="제목 검색"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && setSearch(searchInput.trim())}
            className={styles.filterSearch}
          />
          <button onClick={() => setSearch(searchInput.trim())} className={styles.searchBtn}>검색</button>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>수신시각</th><th>발신자</th><th>제목</th><th>키워드</th>
              <th>수신자 상태</th><th>관리</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className={styles.empty}>알림 내역이 없습니다.</td></tr>
            )}
            {rows.map((r) => (
              <tr
                key={r.id}
                onClick={() => openDetail(r)}
                style={{ cursor: "pointer", fontWeight: r.is_read ? 400 : 700 }}
              >
                <td>{new Date(r.received_at).toLocaleString("ko-KR")}</td>
                <td className={styles.vendor}>{r.sender}</td>
                <td className={styles.title}>{r.subject}</td>
                <td><span className={`${styles.badge} ${styles.badgeNone}`}>{r.matched_keyword}</span></td>
                <td style={{ fontSize: 12, whiteSpace: "nowrap" }}>{receiptSummary(r.receipts, myUserId)}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div className={styles.actionGroup}>
                    {r.my_status === "pending" && (
                      <button
                        className={styles.viewBtn}
                        style={{ width: "auto", padding: "0 10px", whiteSpace: "nowrap" }}
                        disabled={confirmingId === r.id}
                        onClick={() => handleConfirm(r)}
                        title="완료 처리"
                      >
                        {confirmingId === r.id ? "⏳" : "완료"}
                      </button>
                    )}
                    <button
                      className={styles.viewBtn}
                      style={{ width: "auto", padding: "0 10px", whiteSpace: "nowrap" }}
                      onClick={() => openMailSearch(r.subject)}
                      title="네이버 메일에서 제목으로 검색"
                    >
                      메일 확인
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detailRow && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}
          {...detailBackdrop}
        >
          <div
            style={{ background: "#fff", borderRadius: 10, padding: 24, width: 420, maxWidth: "90vw" }}
          >
            <h3 style={{ marginTop: 0, whiteSpace: "nowrap" }}>메일 알림 상세</h3>
            <div style={{ display: "grid", gridTemplateColumns: "80px 1fr", rowGap: 8, fontSize: 14 }}>
              <div style={{ color: "#6c757d", whiteSpace: "nowrap" }}>제목</div><div>{detailRow.subject}</div>
              <div style={{ color: "#6c757d", whiteSpace: "nowrap" }}>발신자</div><div>{detailRow.sender}</div>
              <div style={{ color: "#6c757d", whiteSpace: "nowrap" }}>수신시각</div><div>{new Date(detailRow.received_at).toLocaleString("ko-KR")}</div>
              <div style={{ color: "#6c757d", whiteSpace: "nowrap" }}>키워드</div><div>{detailRow.matched_keyword}</div>
              <div style={{ color: "#6c757d", whiteSpace: "nowrap" }}>수신자</div>
              <div>
                {detailRow.receipts.map((r, i) => (
                  <div key={i} style={{ color: STATUS_COLOR[r.status], whiteSpace: "nowrap" }}>
                    {r.user_id === myUserId ? "나" : r.user_name} — {STATUS_LABEL[r.status] || r.status}
                  </div>
                ))}
              </div>
            </div>
            <div style={{ marginTop: 12, padding: 10, background: "#f8f9fa", borderRadius: 6, fontSize: 12, color: "#6c757d" }}>
              본문/첨부는 저장하지 않습니다 — 메일함에서 확인하세요.
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
              {detailRow.my_status === "pending" && (
                <button
                  className={styles.viewBtn}
                  style={{ width: "auto", padding: "0 14px", whiteSpace: "nowrap" }}
                  onClick={() => handleConfirm(detailRow)}
                >
                  완료
                </button>
              )}
              <button
                className={styles.viewBtn}
                style={{ width: "auto", padding: "0 14px", whiteSpace: "nowrap" }}
                onClick={() => openMailSearch(detailRow.subject)}
              >
                메일 확인
              </button>
              <button
                className={styles.deleteBtn}
                style={{ width: "auto", padding: "0 14px", whiteSpace: "nowrap" }}
                onClick={() => setDetailRow(null)}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
